import {
  actions as GLOBAL_MODALS,
  GLOBAL_MODALS_KEYS,
} from "@src/Actions/Reducers/GlobalModalsReducer"
import { Divider } from "@src/Components/Common"
import { usePortalSize } from "@src/Components/Common/usePortalSize"
import { Section } from "@src/Components/StyledUtils"
import { TextEditor } from "@src/Components/TextEditor"
import Tooltip from "@src/Components/Tooltip"
import { Content, ContentArea, Panel } from "@src/Components/View"
import Waiting from "@src/Components/Waiting"
import { MOBILE_BREAKPOINT } from "@src/Constants"
import {
  DateFormat,
  useUserLocaleDateFormatting,
} from "@src/Hooks/useUserLocaleDateFormatting"
import { tState } from "@src/Model/Model"
import { getCyberConciergeWeaknessWatcher } from "@src/Sagas"
import { colors } from "@src/theme"
import { ViewTypes } from "@src/types"
import { FaIcon } from "@zeguro/component-library"
import { Button } from "@zeguro/component-library"
import { Typography } from "@zeguro/component-library"
import * as React from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useRouteMatch } from "react-router-dom"

import { STATUS_MAP } from "../constants"
import { Badge } from "../WeaknessesList/styles"
import {
  LastUpdatedText,
  NoteSection,
  StatusText,
  StyledBreadcrumbs,
  StyledLink,
} from "./styles"

const statusHints = {
  Info: "Informational findings that don't pose a direct security risk but may be useful for awareness",
  Low: "Issues with minimal impact, likely requiring specific conditions or local access to exploit",
  Medium:
    "Issues that could pose a threat but may be difficult to exploit or have limited impact",
  High: "Significant issues that likely lead to system compromise and require prompt attention",
  Critical:
    "Severe issues requiring immediate attention, likely allow easy remote exploitation with severe consequences",
}

const WeaknessDetails = () => {
  const history = useHistory()
  const match = useRouteMatch<{ weaknessId: string }>()
  const dispatch = useDispatch()
  const { clientWidth } = usePortalSize()
  const weaknessId = match.params.weaknessId
  const { data, isLoading } = useSelector(
    (store: tState) => store.cyberConcierge?.getCyberConciergeWeakness,
  )
  const { emailDomain } = useSelector((state: tState) => state.brand)

  const { formatDatePST } = useUserLocaleDateFormatting()

  const BREADCRUMBS = [
    {
      text: "Security Weaknesses",
      onClick: () => history.push(`${ViewTypes.cyberConcierge}/weaknesses`),
    },
    { text: "Weakness Details" },
  ]

  const formattedReportedOn =
    data?.reportedOn && formatDatePST(data?.reportedOn, DateFormat.DateTime)

  const severity = data?.severity
  const status = data?.status
  const statusUpdatedOn = data?.statusUpdatedOn

  const downloadPdf = () => {
    if (!data) return
    const formattedDate = formatDatePST(data.reportedOn, DateFormat.Date)

    const reportName = `SecurityWeakness_${formattedDate}_${weaknessId}.pdf`
    window.open(`/api/cyberConcierge/weaknessPdf/${weaknessId}/${reportName}`)
  }

  React.useEffect(() => {
    dispatch(getCyberConciergeWeaknessWatcher(weaknessId))
  }, [])

  if (isLoading) {
    return <Waiting text="Loading..." />
  }

  return (
    <ContentArea>
      <Panel>
        <Content>
          <Section flex="space-between center" flexWrap="wrap" gap="8px">
            <StyledBreadcrumbs items={BREADCRUMBS} />
            {clientWidth >= MOBILE_BREAKPOINT && (
              <Section flex="flex-end center" flexGrow="1">
                <Button
                  variant="secondary"
                  width="150px"
                  onClick={() =>
                    dispatch(
                      GLOBAL_MODALS.toggleGlobalModal(
                        GLOBAL_MODALS_KEYS.isContactCyberConcierge,
                      ),
                    )
                  }
                >
                  <Section flex="center center">
                    <FaIcon
                      iconType="fas"
                      iconName="hand-paper"
                      margin="0 6px 0 0"
                    />
                    need help
                  </Section>
                </Button>
                <Button
                  variant="secondary"
                  margin="0 0 0 20px"
                  width="180px"
                  onClick={downloadPdf}
                >
                  <Section flex="center center" gap="6px">
                    <img
                      src="/images/cyberconcierge/filePdf.svg"
                      alt="file pdf"
                    />
                    Download PDF
                  </Section>
                </Button>
              </Section>
            )}
          </Section>
          {clientWidth < MOBILE_BREAKPOINT && (
            <NoteSection margin="24 0 0 0">
              <FaIcon
                iconType="far"
                iconName="exclamation-triangle"
                color={colors.red}
                margin="0 16px 0 0"
              />
              This document may contain sensitive information. Be careful who
              you share it with.
            </NoteSection>
          )}
          {clientWidth < MOBILE_BREAKPOINT && (
            <Section margin="20 0 0 0" flex="flex-start center">
              <Button
                variant="secondary"
                width={"150px"}
                onClick={() =>
                  dispatch(
                    GLOBAL_MODALS.toggleGlobalModal(
                      GLOBAL_MODALS_KEYS.isContactCyberConcierge,
                    ),
                  )
                }
              >
                <Section flex="center center">
                  <FaIcon
                    iconType="fas"
                    iconName="hand-paper"
                    margin="0 6px 0 0"
                  />
                  need help
                </Section>
              </Button>
              <Button
                variant="secondary"
                margin="0 0 0 20px"
                width={"180px"}
                onClick={downloadPdf}
              >
                <Section flex="center center" gap="6px">
                  <img
                    src="/images/cyberconcierge/filePdf.svg"
                    alt="file pdf"
                  />
                  Download PDF
                </Section>
              </Button>
            </Section>
          )}
          {clientWidth >= MOBILE_BREAKPOINT && (
            <NoteSection margin="24 0 0 0">
              <FaIcon
                iconType="far"
                iconName="exclamation-triangle"
                color={colors.red}
                margin="0 12px 0 0"
              />
              This document may contain sensitive information. Be careful who
              you share it with.
            </NoteSection>
          )}
          <Section
            flex={
              clientWidth < MOBILE_BREAKPOINT
                ? "flex-start flex-start column"
                : "space-between flex-end"
            }
            flexWrap="wrap"
            gap="12px"
            margin="24 0 0"
          >
            <Typography variant="heading2" color={colors.black50}>
              {data?.title}
            </Typography>
            <Section color={colors.black50} flexGrow="1" align="right">
              <strong>Reported On: </strong> {formattedReportedOn}
            </Section>
          </Section>
          <Section
            flex={
              clientWidth < MOBILE_BREAKPOINT
                ? "flex-start flex-start column"
                : "space-between flex-end"
            }
            margin="12 0 0"
            gap="12px"
          >
            <Section flex="flex-start flex-end">
              <strong>Severity:</strong>
              {severity && (
                <>
                  <Badge
                    color={severity as any}
                    margin="0 0 0 10px"
                    cursor="help"
                    data-tooltip-id="status-hint-tooltip"
                  >
                    {severity}
                  </Badge>
                  <Tooltip
                    id="status-hint-tooltip"
                    type="dark"
                    content={<p>{statusHints[severity]}</p>}
                  />
                </>
              )}
            </Section>
            <Section flex="flex-end" gap="6px">
              <strong>Status: </strong>
              {status === "No Status" ? (
                "—"
              ) : (
                <>
                  <StatusText data-tooltip-id="status-tooltip">
                    {status}
                  </StatusText>
                  {status && (
                    <Tooltip
                      id="status-tooltip"
                      pointerEvents="auto"
                      content={
                        <div>
                          <p>{STATUS_MAP[status]}</p>
                          <br />
                          {statusUpdatedOn && (
                            <LastUpdatedText>
                              Last updated:&nbsp;
                              {formatDatePST(
                                statusUpdatedOn,
                                DateFormat.DateTime,
                              )}
                              &nbsp; by Cyber Concierge
                            </LastUpdatedText>
                          )}
                          <p>
                            Need to update the status?&nbsp;&nbsp;
                            <FaIcon iconType="far" iconName="envelope" />
                            &nbsp;
                            <StyledLink
                              isUnderline
                              href={`mailto:support@${emailDomain}`}
                            >
                              Cyber Concierge
                            </StyledLink>
                          </p>
                        </div>
                      }
                    />
                  )}
                </>
              )}
            </Section>
          </Section>
          <Divider margin="20px 0 8px" />
          <TextEditor body={data?.body} />
        </Content>
      </Panel>
    </ContentArea>
  )
}

export default WeaknessDetails
